import { useApi } from "./useApi";
import { companyService } from "../services/companies";
import {
  Company,
  CompanyCreatePayload,
  CompanyUpdatePayload,
  DoctorCompaniesResponse,
} from "../types/company.types";
import { useSession } from "../context/SessionContext";
import { SUCCESS_MESSAGES } from "../i18n/success.pt-BR";

export function useCompanies() {
  const { metadata } = useSession();

  const searchApi = useApi<Company[]>();
  const createApi = useApi<Company>();
  const updateApi = useApi<Company>();
  const listApi   = useApi<DoctorCompaniesResponse>();
  const linkApi   = useApi<void>();
  const unlinkApi = useApi<void>();

  // Autocomplete: não mostra toast de erro a cada tecla digitada,
  // uma falha de rede aqui não deve interromper o usuário.
  const searchCompanies = async (query: string) => {
    return searchApi.request(
      () => companyService.search(searchApi.apiFetch, query),
      { showErrorToast: false },
    );
  };

  const createCompany = async (payload: CompanyCreatePayload) => {
    return createApi.request(
      () => companyService.create(createApi.apiFetch, payload),
    );
  };

  // Edição é sobre a empresa em si (não sobre o vínculo) — pode afetar outros
  // médicos que compartilham a mesma empresa. O `detail` de um 422 (ex: CNPJ
  // inválido) já chega pronto pro usuário via toast automático do useApi.
  const updateCompany = async (companyId: number, payload: CompanyUpdatePayload) => {
    return updateApi.request(
      () => companyService.update(updateApi.apiFetch, companyId, payload),
    );
  };

  const listMyCompanies = async () => {
    if (!metadata.email) return null;

    return listApi.request(
      () => companyService.listForDoctor(listApi.apiFetch, metadata.email!),
    );
  };

  const linkCompany = async (companyId: number) => {
    if (!metadata.email) return null;

    return linkApi.request(
      () => companyService.linkToDoctor(linkApi.apiFetch, metadata.email!, { company_id: companyId }),
      { successMessage: SUCCESS_MESSAGES.companyLinked },
    );
  };

  const unlinkCompany = async (companyId: number) => {
    if (!metadata.email) return null;

    return unlinkApi.request(
      () => companyService.unlinkFromDoctor(unlinkApi.apiFetch, metadata.email!, companyId),
      { successMessage: SUCCESS_MESSAGES.companyUnlinked },
    );
  };

  return {
    searchCompanies,
    createCompany,
    updateCompany,
    listMyCompanies,
    linkCompany,
    unlinkCompany,
    searching: searchApi.loading,
    creating:  createApi.loading,
    updating:  updateApi.loading,
    listing:   listApi.loading,
    linking:   linkApi.loading,
    unlinking: unlinkApi.loading,
  };
}
